import React, { useState, useEffect, useMemo } from "react";
import { useField } from "formik";
import {
  Box,
  Flex,
  Text,
  Button,
  TextField,
  SegmentedControl,
  IconButton,
  Badge,
  Grid,
  Callout,
} from "@radix-ui/themes";
import { Sparkles, Trash2, Plus, AlertCircle } from "lucide-react";
import { useEntities } from "@/hooks/useCommonQueries";
import CodeField from "@/components/form/CodeField";
import { ANYTHINK_TYPE_DEFINITIONS } from "../anythink-types";
import { WorkflowStep } from "@/types/breeze/Workflow";
import VariableMenu from "./VariableMenu";

interface PayloadFieldMapperProps {
  name: string;
  entityName: string;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
  label?: string;
  required?: boolean;
}

export default function PayloadFieldMapper({
  name,
  entityName,
  existingSteps = [],
  triggerEntity,
  label = "Payload",
  required = false,
}: PayloadFieldMapperProps) {
  const [field, meta, helpers] = useField<string>(name);
  const { data: entities } = useEntities();

  // Find the entity schema definition
  const entity = useMemo(() => {
    return entities?.find((e) => e.name === entityName);
  }, [entities, entityName]);

  const [mode, setMode] = useState<"visual" | "json">("visual");
  const [visualValues, setVisualValues] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<string[]>([]);
  const [newFieldName, setNewFieldName] = useState("");
  const [showFieldNameError, setShowFieldNameError] = useState(false);

  // Sync Formik JSON changes to local state on initial load or reset
  const hasInitialized = React.useRef(false);
  const lastSyncedValue = React.useRef<string | undefined>(undefined);
  const lastSyncedEntityFieldsKey = React.useRef<string>("");

  useEffect(() => {
    const entityFieldsKey = entity?.fields?.map((f) => f.name).join(",") || "";
    const hasEntityChanged = lastSyncedEntityFieldsKey.current !== entityFieldsKey;

    // Prevent infinite loop by checking if value is unchanged
    if (field.value === lastSyncedValue.current && !hasEntityChanged) {
      return;
    }

    lastSyncedValue.current = field.value;
    lastSyncedEntityFieldsKey.current = entityFieldsKey;

    // If empty payload, initialize empty states
    if (!field.value) {
      setVisualValues({});
      setCustomFields([]);
      setMode("visual");
      hasInitialized.current = true;
      return;
    }

    try {
      const parsed = JSON.parse(field.value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const initialValues: Record<string, string> = {};
        const initialCustoms: string[] = [];

        // Identify fields in the entity schema, excluding system generated columns
        const entityFields = new Set(
          entity?.fields
            ?.map((f) => f.name)
            ?.filter((name) => name !== "id" && name !== "created_at" && name !== "updated_at") || []
        );

        Object.entries(parsed).forEach(([key, val]) => {
          if (key === "id" || key === "created_at" || key === "updated_at") return;
          let valString = "";
          if (val === null || val === undefined) {
            valString = "";
          } else if (typeof val === "object") {
            valString = JSON.stringify(val);
          } else {
            valString = String(val);
          }

          initialValues[key] = valString;
          if (!entityFields.has(key)) {
            initialCustoms.push(key);
          }
        });

        setVisualValues(initialValues);
        setCustomFields(initialCustoms);
        // Only set mode once to avoid overriding manual switches
        if (!hasInitialized.current) {
          setMode("visual");
          hasInitialized.current = true;
        }
      } else {
        if (!hasInitialized.current) {
          setMode("json");
          hasInitialized.current = true;
        }
      }
    } catch (e) {
      if (!hasInitialized.current) {
        setMode("json");
        hasInitialized.current = true;
      }
    }
  }, [field.value, entityName, entity]);

  // Serializes the local visual state into Formik value
  const serializeVisualState = (
    currentVals: Record<string, string>,
    customList: string[],
  ) => {
    const payloadObject: Record<string, any> = {};

    // Get all valid fields (entity fields + active custom fields), excluding system generated columns
    const entityFields = entity?.fields
      ?.map((f) => f.name)
      ?.filter((name) => name !== "id" && name !== "created_at" && name !== "updated_at") || [];
    const allValidKeys = new Set([...entityFields, ...customList]);

    Object.entries(currentVals).forEach(([key, value]) => {
      if (!allValidKeys.has(key) || value === "") return;

      // Type parsing helper for non-template values
      let parsedValue: any = value;
      if (typeof value === "string" && !value.includes("{{")) {
        const trimmed = value.trim();
        if (trimmed === "true") parsedValue = true;
        else if (trimmed === "false") parsedValue = false;
        else if (trimmed === "null") parsedValue = null;
        else if (/^-?\d+$/.test(trimmed)) {
          const num = parseInt(trimmed, 10);
          if (!isNaN(num)) parsedValue = num;
        } else if (/^-?\d+\.\d+$/.test(trimmed)) {
          const num = parseFloat(trimmed);
          if (!isNaN(num)) parsedValue = num;
        } else if (
          (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
          (trimmed.startsWith("[") && trimmed.endsWith("]"))
        ) {
          try {
            parsedValue = JSON.parse(trimmed);
          } catch {
            // Keep as string if parsing fails
          }
        }
      }

      payloadObject[key] = parsedValue;
    });

    const serialized = JSON.stringify(payloadObject, null, 2);
    lastSyncedValue.current = serialized;
    helpers.setValue(serialized);
  };

  // Handles updating a single visual field
  const handleFieldChange = (fieldName: string, value: string) => {
    const nextVals = { ...visualValues, [fieldName]: value };
    setVisualValues(nextVals);
    serializeVisualState(nextVals, customFields);
  };

  // Adds a custom key-value field
  const handleAddCustomField = () => {
    const formattedName = newFieldName.trim().replace(/[^a-zA-Z0-9_]/g, "_");
    if (!formattedName) return;

    const entityFields = new Set(entity?.fields?.map((f) => f.name) || []);
    const systemFields = new Set(["id", "created_at", "updated_at"]);
    if (
      entityFields.has(formattedName) ||
      systemFields.has(formattedName) ||
      customFields.includes(formattedName)
    ) {
      setShowFieldNameError(true);
      return;
    }

    setShowFieldNameError(false);
    const nextCustoms = [...customFields, formattedName];
    setCustomFields(nextCustoms);

    const nextVals = { ...visualValues, [formattedName]: "" };
    setVisualValues(nextVals);
    setNewFieldName("");
    serializeVisualState(nextVals, nextCustoms);
  };

  // Removes a custom field
  const handleRemoveCustomField = (fieldName: string) => {
    const nextCustoms = customFields.filter((cf) => cf !== fieldName);
    setCustomFields(nextCustoms);

    const nextVals = { ...visualValues };
    delete nextVals[fieldName];
    setVisualValues(nextVals);
    serializeVisualState(nextVals, nextCustoms);
  };

  // Handles switching editor tabs
  const handleModeChange = (newMode: "visual" | "json") => {
    if (newMode === "visual") {
      try {
        const parsed = JSON.parse(field.value || "{}");
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          setMode("visual");
        } else {
          // If JSON contains array or primitives, warn/block switching
          alert(
            "Cannot switch to Visual Mapper: The current payload format is not a single flat/nested object.",
          );
        }
      } catch {
        alert(
          "Cannot switch to Visual Mapper: Please fix the JSON syntax errors first.",
        );
      }
    } else {
      setMode("json");
    }
  };

  const insertVariable = (fieldName: string, variableText: string) => {
    handleFieldChange(fieldName, variableText);

    // Refocus the input element
    const inputEl = document.getElementById(
      `mapper-input-${fieldName}`,
    ) as HTMLInputElement | null;
    if (inputEl) {
      setTimeout(() => {
        inputEl.focus();
        inputEl.setSelectionRange(variableText.length, variableText.length);
      }, 50);
    }
  };

  return (
    <Box mb="4" width="100%">
      <Flex justify="between" align="center" mb="2">
        <Text size="2" weight="medium">
          {label} {required && <Text color="red">*</Text>}
        </Text>
        <SegmentedControl.Root
          value={mode}
          onValueChange={(val) => handleModeChange(val as "visual" | "json")}
          size="1"
        >
          <SegmentedControl.Item value="visual">
            Visual Mapper
          </SegmentedControl.Item>
          <SegmentedControl.Item value="json">
            JSON Editor
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      {mode === "visual" ? (
        <Box
          style={{
            border: "1px solid var(--gray-6)",
            borderRadius: "var(--radius-3)",
            padding: "16px",
            backgroundColor: "var(--color-bg)",
          }}
        >
          {!entity ? (
            <Callout.Root size="1" color="amber" mb="3">
              <Callout.Icon>
                <AlertCircle size={16} />
              </Callout.Icon>
              <Callout.Text>
                Please select an Entity above to enable visual field mapping.
              </Callout.Text>
            </Callout.Root>
          ) : (
            <Flex direction="column" gap="3">
              <Text size="1" color="gray" mb="1">
                Map fields for <strong>{entity.name}</strong>. Use the variables
                menu{" "}
                <Sparkles
                  size={11}
                  style={{ display: "inline", verticalAlign: "middle" }}
                />{" "}
                to reference outputs from previous steps.
              </Text>

              {/* Entity Fields Listing */}
              {entity.fields
                ?.filter((f) => f.name !== "id" && f.name !== "created_at" && f.name !== "updated_at")
                ?.map((fieldDef) => {
                const isFieldRequired =
                  fieldDef.is_required && !fieldDef.default_value;
                return (
                  <Grid
                    columns="1fr 2fr"
                    gap="3"
                    align="center"
                    key={fieldDef.name}
                    style={{
                      borderBottom: "1px solid var(--gray-3)",
                      paddingBottom: "8px",
                    }}
                  >
                    {/* Field info */}
                    <Flex direction="column" gap="1">
                      <Flex align="center" gap="1">
                        <Text size="2" weight="medium">
                          {fieldDef.name}
                        </Text>
                        {isFieldRequired && (
                          <Text color="red" size="2">
                            *
                          </Text>
                        )}
                      </Flex>
                      <Flex gap="1">
                        <Badge size="1" color="blue" variant="soft">
                          {fieldDef.database_type}
                        </Badge>
                        {fieldDef.is_unique && (
                          <Badge size="1" color="yellow" variant="soft">
                            unique
                          </Badge>
                        )}
                      </Flex>
                    </Flex>

                    {/* Field Value Input */}
                    <Flex gap="2" align="center">
                      <TextField.Root
                        id={`mapper-input-${fieldDef.name}`}
                        placeholder={
                          fieldDef.default_value || "Enter value or mapping..."
                        }
                        value={visualValues[fieldDef.name] || ""}
                        onChange={(e) =>
                          handleFieldChange(fieldDef.name, e.target.value)
                        }
                        style={{ width: "100%", fontFamily: "monospace" }}
                      />
                      <VariableMenu
                        existingSteps={existingSteps}
                        triggerEntity={triggerEntity}
                        onInsert={(text) => insertVariable(fieldDef.name, text)}
                        showLoopContext={true}
                      />
                    </Flex>
                  </Grid>
                );
              })}

              {/* Custom Fields Section */}
              {customFields.map((cf) => (
                <Grid
                  columns="1fr 2fr"
                  gap="3"
                  align="center"
                  key={cf}
                  style={{
                    borderBottom: "1px solid var(--gray-3)",
                    paddingBottom: "8px",
                  }}
                >
                  <Flex align="center" justify="between">
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium" color="orange">
                        {cf} (custom)
                      </Text>
                    </Flex>
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="red"
                      size="1"
                      aria-label="Delete custom field"
                      onClick={() => handleRemoveCustomField(cf)}
                    >
                      <Trash2 size={13} />
                    </IconButton>
                  </Flex>

                  <Flex gap="2" align="center">
                    <TextField.Root
                      id={`mapper-input-${cf}`}
                      placeholder="Enter custom field value..."
                      value={visualValues[cf] || ""}
                      onChange={(e) => handleFieldChange(cf, e.target.value)}
                      style={{ width: "100%", fontFamily: "monospace" }}
                    />
                    <VariableMenu
                      existingSteps={existingSteps}
                      triggerEntity={triggerEntity}
                      onInsert={(text) => insertVariable(cf, text)}
                      showLoopContext={true}
                    />
                  </Flex>
                </Grid>
              ))}

              {/* Add Custom Field Form */}
              <Flex gap="2" mt="2" align="end">
                <Box style={{ flexGrow: 1 }}>
                  <Text
                    size="1"
                    color="gray"
                    mb="1"
                    style={{ display: "block" }}
                  >
                    Add Custom Field Name
                  </Text>
                  <TextField.Root
                    placeholder="e.g. metadata_tag"
                    value={newFieldName}
                    onChange={(e) => {
                      setNewFieldName(e.target.value);
                      setShowFieldNameError(false);
                    }}
                  />
                  {showFieldNameError && (
                    <Text size="1" color="red" mt="1">
                      Field name already exists.
                    </Text>
                  )}
                </Box>
                <Button
                  type="button"
                  variant="soft"
                  onClick={handleAddCustomField}
                  disabled={!newFieldName.trim()}
                >
                  <Plus size={14} style={{ marginRight: 4 }} /> Add Field
                </Button>
              </Flex>
            </Flex>
          )}
        </Box>
      ) : (
        <CodeField
          name={name}
          label=""
          language="application/json"
          required={required}
          isEditing={true}
          typeDefinitions={ANYTHINK_TYPE_DEFINITIONS}
          disableValidation={true}
        />
      )}

      {meta.touched && meta.error && (
        <Text size="1" color="red" mt="1" style={{ display: "block" }}>
          {meta.error}
        </Text>
      )}
    </Box>
  );
}
